import { debounce } from 'lodash';
import { usePatientInsightSettings } from '../../../../../context';
import type { SleepInterventionType } from '../../../../../lib/responses/getInsightSettings';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { CaremapprSwitch } from '../../../Swich/CaremapprSwitch';
import { usePatientProfile } from '../../../ProfileBar/hooks';

interface InsightSettingsItem extends SleepInterventionType {
  enabled: boolean;
}
const OSAInterventionSettings = () => {
  const { isLoading } = usePatientInsightSettings();

  if (isLoading) {
    return (
      <div className="my-4">
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <div className="d-flex flex-column">
      <span
        className="my-4"
        style={{
          fontSize: '18px',
          fontWeight: '600',
          fontFamily: 'Open Sans, sans-serif',
        }}
      >
        Intervention settings
      </span>

      <InterventionSettingsContent />
    </div>
  );
};

export default OSAInterventionSettings;

const InterventionSettingsContent = () => {
  const { insightSettings } = usePatientInsightSettings();
  const { patientProfile } = usePatientProfile();
  const settings = insightSettings?.settings || [];
  const groupInsightSettings: InsightSettingsItem[] =
    insightSettings?.types?.map((settingType) => {
      const foundSettings = settings.filter(
        (setting) => setting.details.id === settingType.id,
      );
      const latestSetting =
        foundSettings.length === 1
          ? foundSettings[0]
          : foundSettings.sort((s1, s2) =>
              s2.created_at.localeCompare(s1.created_at),
            )?.[0];

      return {
        ...settingType,
        enabled: latestSetting?.settings.changeInsight || false,
      };
    }) || [];

  const patientFirstName = patientProfile.first_name;

  return (
    <div className="pt-2 pb-5">
      <div
        className="mb-3"
        style={{
          color: 'rgba(106, 114, 119, 1)',
          fontFamily: 'Open Sans, sans-serif',
          fontSize: '12px',
          fontWeight: '400',
        }}
      >
        <span>
          The following interventions can be applied to{' '}
          {patientFirstName || 'Patient'}’s profile. The set of inteventions can
          be updated in Webiny at any time.
        </span>
      </div>
      <div className="d-flex flex-column gap-4">
        {groupInsightSettings.map((setting) => (
          <InterventionSettingItem key={setting.id} {...setting} />
        ))}
      </div>
      <hr
        style={{
          marginTop: '32px',
          color: 'rgb(151, 151, 151)',
          height: '1px',
          border: '1px dashed',
        }}
      />
    </div>
  );
};

const InterventionSettingItem = ({
  id,
  title,
  description,
  enabled,
}: InsightSettingsItem) => {
  const { applyInsightSettings } = usePatientInsightSettings();
  const onToggle = debounce((checked: boolean) => {
    applyInsightSettings({
      settings: [
        {
          details: { type: 'sleepIntervention', id },
          settings: { changeInsight: checked },
        },
      ],
    });
  }, 1500);

  return (
    <div className="d-flex align-items-start gap-2">
      <CaremapprSwitch defaultChecked={enabled} onChange={onToggle} />
      <div className="d-flex flex-column gap-2">
        <span
          style={{
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'Open Sans, sans-serif',
            color: 'rgba(49, 49, 49, 1)',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '13px',
            fontWeight: '400',
            fontFamily: 'Open Sans, sans-serif',
            color: 'rgba(106, 114, 119, 1)',
          }}
        >
          {description}
        </span>
      </div>
    </div>
  );
};
